import React from 'react';
import { getMerkur } from '@merkur/core';
import { loadStyleAssets, loadScriptAssets } from '@merkur/integration';

function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
const SelectorIdentifierMap = Object.freeze({
  '#': 'id',
  '.': 'className'
});

/**
 * Parses container selector into element property. Currently
 * only works with IDs and classnames.
 *
 * @param {string} [containerSelector='']
 * @return {{ string: string }} Property name and value tuple.
 */
function selectorToAttribute(containerSelector = '') {
  if (typeof containerSelector !== 'string' || containerSelector.length === 0) {
    return {};
  }
  let lastIdentifierIndex = -1;
  for (let identifierKey in SelectorIdentifierMap) {
    let curLastIndex = containerSelector.lastIndexOf(identifierKey);
    if (lastIdentifierIndex < curLastIndex) {
      lastIdentifierIndex = curLastIndex;
    }
  }
  const identifier = containerSelector[lastIdentifierIndex];
  const selectorName = containerSelector.substr(lastIdentifierIndex + 1);
  return {
    [SelectorIdentifierMap[identifier]]: selectorName
  };
}

/**
 * Returns empty <div> with SSR html as it's contents (if provided) and
 * container selector defined, which is used as merkur widget wrapper.
 *
 * @param {{ html, containerSelector, children }}
 * @return {import('react').ReactElement}
 */
function WidgetWrapper({
  html,
  containerSelector,
  children
}) {
  const selector = selectorToAttribute(containerSelector);
  return html ? /*#__PURE__*/React.createElement("div", _extends({}, selector, {
    dangerouslySetInnerHTML: {
      __html: html
    }
  })) : /*#__PURE__*/React.createElement("div", selector, children);
}
var WidgetWrapper$1 = /*#__PURE__*/React.memo(WidgetWrapper);

class AbstractMerkurWidget extends React.Component {
  /**
   * Checks if widget has changed, e.g has different name or version.
   *
   * @param {{ name: string, version: string }} props
   * @param {{ name: string, version: string }} nextProps
   * @return {boolean} true if both inputs have name and version defined and any of them changed.
   */
  static hasWidgetChanged(props, nextProps) {
    return !!(props && props.name && props.version && nextProps && nextProps.name && nextProps.version && (props.version !== nextProps.version || props.name !== nextProps.name));
  }

  /**
   * Validates widget properties, widget properties should not be an empty object.
   *
   * @param {object} widgetProperties
   * @return {boolean} validation result.
   */
  static validateProperties(widgetProperties) {
    return !!(widgetProperties && widgetProperties.name && widgetProperties.version);
  }

  /**
   * Widget SSR rendered html (from server response).
   */
  get html() {
    throw new Error('The html getter is abstract and must be overridden');
  }

  /**
   * Widget container element.
   */
  get container() {
    throw new Error('The container getter is abstract and must be overridden');
  }
  constructor(props) {
    super(props);
    this._isMounted = false;
    this._html = null;
  }
  componentDidMount() {
    this._isMounted = true;
  }

  /**
   * Renders fallback (children) when widget is already mounted
   * but not ready or any error has occurred.
   *
   * @return {React.ReactElement|null}
   */
  _renderFallback() {
    const {
      children
    } = this.props;
    const {
      encounteredError = null
    } = this.state || {};
    if (typeof children === 'function') {
      return children({
        error: encounteredError
      });
    } else if (/*#__PURE__*/React.isValidElement(children)) {
      return children;
    }
    return null;
  }

  /**
   * Clears cached SSR rendered html.
   */
  _clearCachedHtml() {
    this._html = null;
  }

  /**
   * @return {boolean} true in browser environment.
   */
  _isClient() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  /**
   * @return {string} SSR rendered HTML, html from widgetProperties or ''.
   */
  _getWidgetHTML() {
    if (this._html !== null) {
      return this._html;
    }
    this._html = this.html || this._getSSRHTML();
    return this._html;
  }

  /**
   * Return server-side rendered html, if its the first render on client
   * after SSR.
   *
   * @return {string} server-side rendered html, if it's not available, return empty string.
   */
  _getSSRHTML() {
    if (!this._isMounted && this._isClient()) {
      return this.container?.children?.[0]?.outerHTML || '';
    }
    return '';
  }

  /**
   * Checks if it's the first render after SSR.
   *
   * @return {boolean} true in case of a first render after SSR, otherwise false.
   */
  _isSSRHydrate() {
    return this._getSSRHTML().length > 0;
  }
}

// error event name from @merkur/plugin-error
const MERKUR_ERROR_EVENT_NAME = '@merkur/plugin-error.error';
class MerkurWidget extends AbstractMerkurWidget {
  /**
   * @inheritdoc
   */
  get html() {
    return this.props.widgetProperties?.html || null;
  }

  /**
   * @inheritdoc
   */
  get container() {
    return this._isClient() && document?.querySelector(this.props.widgetProperties?.containerSelector) || null;
  }
  constructor(props) {
    super(props);
    this._widget = null;
    // emitter has widget as first argument, error as second
    this._handleClientError = (_widget, error) => this._handleError(error);
    this.state = {
      encounteredError: false,
      assetsLoaded: false,
      cachedWidgetMeta: null
    };
  }

  /**
   * In case widget props change to new widget, we need to reset
   * state before next render. This enables us to immediately render
   * fallback without first rendering empty widget wrapper.
   *
   * @param {object} nextProps
   * @param {object} prevState
   */
  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState && nextProps && AbstractMerkurWidget.validateProperties(nextProps.widgetProperties)) {
      const {
        version,
        name
      } = nextProps.widgetProperties;

      // Cache widget meta data (name & version)
      if (!prevState.cachedWidgetMeta) {
        return {
          cachedWidgetMeta: {
            name,
            version
          }
        };
      }

      // Replace cached widget meta data with new ones and reset state
      if (AbstractMerkurWidget.hasWidgetChanged(prevState.cachedWidgetMeta, nextProps.widgetProperties)) {
        return {
          encounteredError: false,
          assetsLoaded: false,
          cachedWidgetMeta: {
            name,
            version
          }
        };
      }
    }
    return null;
  }

  /**
   * Component should be updated only in these cases:
   *  1) State of MerkurWidget has changed (excluding { @code this.state.cachedWidgetMeta }).
   *  2) Component has no props.widgetProperties.
   *  3) Widget properties changed (name or version).
   *
   * In case { @code widgetProperties.state } or { @code widgetProperties.props }
   * the component should still not update, however we should update the widget
   * state and props using appropriate methods on the widget instance.
   *
   * @param {object} nextProps
   * @param {object} nextState
   */
  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.assetsLoaded !== nextState.assetsLoaded || this.state.encounteredError !== nextState.encounteredError || !AbstractMerkurWidget.validateProperties(this.props.widgetProperties) || AbstractMerkurWidget.hasWidgetChanged(this.props.widgetProperties, nextProps.widgetProperties)) {
      return true;
    }
    if (this._widget && nextProps.widgetProperties && nextProps.widgetProperties.props) {
      if (Object.keys(nextProps.widgetProperties.props).length !== Object.keys(this.props.widgetProperties.props).length) {
        /**
         * In case the new props are only subset of the existing ones
         * and all existing values are the same, simple key/value
         * comparison below would not work.
         */
        this._widget.setProps(nextProps.widgetProperties.props);
        return false;
      } else {
        /**
         * In case the length of both property instances is the same
         * we need to shallow compare their values.
         */
        for (let key in nextProps.widgetProperties.props) {
          if (!this.props.widgetProperties || !this.props.widgetProperties.props || nextProps.widgetProperties.props[key] !== this.props.widgetProperties.props[key]) {
            this._widget.setProps(nextProps.widgetProperties.props);
            return false;
          }
        }
      }
    }
    if (this._widget && nextProps.widgetProperties && nextProps.widgetProperties.state) {
      if (Object.keys(nextProps.widgetProperties.state).length !== Object.keys(this.props.widgetProperties.state).length) {
        this._widget.setState(nextProps.widgetProperties.state);
        return false;
      } else {
        for (let key in nextProps.widgetProperties.state) {
          if (!this.props.widgetProperties || !this.props.widgetProperties.state || nextProps.widgetProperties.state[key] !== this.props.widgetProperties.state[key]) {
            this._widget.setState(nextProps.widgetProperties.state);
            return false;
          }
        }
      }
    }
    return false;
  }

  /**
   * In case of mounting the component, we always try to first load
   * the widget assets. Be it first mount after SSR or mount after
   * first render on client.
   */
  componentDidMount() {
    super.componentDidMount();
    this._loadWidgetAssets();
  }

  /**
   * After the component has been updated, we still need to handle few situations.
   *  1) In case assets have been loaded, we need to mount the widget.
   *  2) Handle situations where either old or new widget properties have changed.
   *
   * @param {object} prevProps
   * @param {object} prevState
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      widgetProperties: currentWidgetProperties
    } = this.props;
    const {
      widgetProperties: prevWidgetProperties
    } = prevProps;

    // 1) Assets have been loaded => mount the widget
    if (this.state.assetsLoaded && prevState.assetsLoaded !== this.state.assetsLoaded) {
      return this._mountWidget();
    }

    // 2.1) In case we receive empty new properties, we need to cleanup.
    if (!AbstractMerkurWidget.validateProperties(currentWidgetProperties) && AbstractMerkurWidget.validateProperties(prevWidgetProperties)) {
      this._removeWidget();
      this.setState({
        encounteredError: false,
        assetsLoaded: false,
        cachedWidgetMeta: null
      });
      return;
    }

    /**
     * 2.2) In case there were no widget properties before, we try to
     * initialize widget first by doing the same as if it first mounted
     * (loading assets into the DOM).
     */
    if (AbstractMerkurWidget.validateProperties(currentWidgetProperties) && !AbstractMerkurWidget.validateProperties(prevWidgetProperties)) {
      return this._loadWidgetAssets();
    }

    /**
     * 2.3) In case widget has changed, first we need to cleanup (remove previous widget),
     * and then we again try to initialize the new widget same way as
     * if it has mounted for the first time (if there are any new widget properties).
     */
    if (AbstractMerkurWidget.hasWidgetChanged(currentWidgetProperties, prevWidgetProperties)) {
      this._removeWidget();
      this._loadWidgetAssets();
      return;
    }
  }

  /**
   * In case of unmounting we only really need to do the cleanup.
   */
  componentWillUnmount() {
    this._removeWidget();
  }

  /**
   * There are two possible outputs from the render method:
   *  1) Fallback is rendered only, when assets are not yet loaded
   *     or there was en error or there are no widget properties.
   *  2) WidgetWrapper is rendered in case of SSR (on server-side),
   *     SSR hydrate, with server-side rendered HTML, when assets
   *     are loaded with widget HTML, which is later mounted.
   *
   * @return {React.ReactElement|null}
   */
  render() {
    const {
      widgetProperties
    } = this.props;
    const {
      encounteredError,
      assetsLoaded
    } = this.state;
    if (!AbstractMerkurWidget.validateProperties(widgetProperties) || encounteredError || this._isClient() && !this._isSSRHydrate() && !assetsLoaded) {
      return this._renderFallback();
    }
    if (!widgetProperties.containerSelector) {
      throw new Error(`The widgetProperties.containerSelector is not defined`);
    }
    const html = this._getWidgetHTML();
    return /*#__PURE__*/React.createElement(React.Fragment, null, (!this._isClient() || this._isSSRHydrate()) && this._renderStyleAssets(), /*#__PURE__*/React.createElement(WidgetWrapper$1, {
      containerSelector: widgetProperties.containerSelector,
      html: html
    }));
  }

  /**
   * Renders widget style assets inline. This is used on SSR
   * and in first render after SSR hydration.
   *
   * @return {[React.ReactElement]}
   */
  _renderStyleAssets() {
    const {
      widgetProperties
    } = this.props;
    const assets = Array.isArray(widgetProperties?.assets) ? widgetProperties.assets : [];
    return assets.filter(asset => ['stylesheet', 'inlineStyle'].includes(asset.type) && asset.source).map((asset, key) => {
      switch (asset.type) {
        case 'stylesheet':
          return /*#__PURE__*/React.createElement("link", {
            rel: "stylesheet",
            href: asset.source,
            key: key
          });
        case 'inlineStyle':
          return /*#__PURE__*/React.createElement("style", {
            key: key,
            dangerouslySetInnerHTML: {
              __html: asset.source
            }
          });
      }
    });
  }
  _handleError(error) {
    if (typeof this.props.onError === 'function' && this.props.onError(error)) {
      return;
    }
    this.setState({
      encounteredError: error
    });
  }

  /**
   * Handles widget unmounting and removal, while removing
   * event listeners, calling unmount on props and widget.
   */
  _removeWidget() {
    if (!this._widget) {
      return;
    }
    if (this.props.onWidgetUnmounting) {
      this.props.onWidgetUnmounting(this._widget);
    }

    // widget might not be using @merkur/plugin-event-emitter
    if (typeof this._widget.off === 'function') {
      this._widget.off(MERKUR_ERROR_EVENT_NAME, this._handleClientError);
    }
    this._widget.unmount();
    this._widget = null;
    this._clearCachedHtml();
  }

  /**
   * Loads widget assets into page.
   */
  _loadWidgetAssets() {
    const {
      widgetProperties
    } = this.props;
    if (!AbstractMerkurWidget.validateProperties(widgetProperties) || this._widget) {
      return;
    }
    return Promise.all([loadStyleAssets(widgetProperties.assets), loadScriptAssets(widgetProperties.assets)]).then(() => new Promise(resolve => {
      this.setState({
        assetsLoaded: true
      }, () => {
        resolve();
      });
    })).catch(error => this._handleError(error));
  }

  /**
   * Creates and mounts widget instance after all resource loaded.
   */
  async _mountWidget() {
    const {
      widgetProperties,
      onWidgetMounted,
      debug = false
    } = this.props;
    if (!AbstractMerkurWidget.validateProperties(widgetProperties) || this._widget) {
      return;
    }
    try {
      const merkur = getMerkur();
      this._widget = await merkur.create(widgetProperties);
      await this._widget.mount();
      if (typeof this._widget.on === 'function') {
        // widget might not be using @merkur/plugin-event-emitter
        this._widget.on(MERKUR_ERROR_EVENT_NAME, this._handleClientError);
      }
      if (typeof onWidgetMounted === 'function') {
        onWidgetMounted(this._widget);
      }
    } catch (error) {
      if (debug) {
        console.warn(error);
      }
    }
  }
}

class MerkurSlot extends AbstractMerkurWidget {
  /**
   * Returns access to current slot properties, based on it's name
   * passed in props.
   */
  get slot() {
    const {
      widgetProperties,
      slotName
    } = this.props;
    return widgetProperties?.slot?.[slotName] ?? null;
  }

  /**
   * @inheritdoc
   */
  get html() {
    return this.slot?.html || null;
  }

  /**
   * @inheritdoc
   */
  get container() {
    return this._isClient() && document?.querySelector(this.slot?.containerSelector) || null;
  }

  /**
   * The component should update only in following cases:
   *  1) Component has no props.widgetProperties.
   *  2) Widget properties changed (name or version).
   *
   * @param {object} nextProps
   */
  shouldComponentUpdate(nextProps) {
    if (!AbstractMerkurWidget.validateProperties(this.props.widgetProperties) || !AbstractMerkurWidget.validateProperties(nextProps.widgetProperties) || AbstractMerkurWidget.hasWidgetChanged(this.props.widgetProperties, nextProps.widgetProperties)) {
      return true;
    }
    return false;
  }

  /**
   * Cleanup when we receive empty widget properties.
   *
   * @param {object} prevProps
   * @param {object} prevState
   */
  componentDidUpdate(prevProps) {
    const {
      widgetProperties: currentWidgetProperties
    } = this.props;
    const {
      widgetProperties: prevWidgetProperties
    } = prevProps;
    if (!AbstractMerkurWidget.validateProperties(currentWidgetProperties) && AbstractMerkurWidget.validateProperties(prevWidgetProperties)) {
      this._removeSlot();
      return;
    }
  }

  /**
   * Cleanup when unmounting
   */
  componentWillUnmount() {
    this._removeSlot();
  }

  /**
   * There are two possible outputs from the render method:
   *  1) Fallback is rendered only, when there are no widget properties.
   *  2) WidgetWrapper is rendered in case of SSR (on server-side),
   *     SSR hydrate - with server-side rendered HTML and on client
   *     (SPA) without HTML.
   *
   * @return {React.ReactElement|null}
   */
  render() {
    const {
      widgetProperties
    } = this.props;
    if (!AbstractMerkurWidget.validateProperties(widgetProperties) || !this.slot) {
      return this._renderFallback();
    }
    if (!this.slot['containerSelector']) {
      throw new Error(`The ${this.slot.name}.containerSelector is not defined`);
    }

    /**
     * In case of SPA rendering, we render fallback (which can also display
     * loading placeholders) inside the component wrapper, until the widget
     * assets are loaded and mounted, which results in overriding the contents
     * of the wrapper (containing fallback) with slot markup. In case of SPA
     * we also don't want to render html to prevent FOUC.
     */
    const isInitialSPARender = this._isClient() && !this._isSSRHydrate();
    const html = isInitialSPARender ? '' : this._getWidgetHTML();
    return /*#__PURE__*/React.createElement(WidgetWrapper$1, {
      containerSelector: this.slot.containerSelector,
      html: html
    }, isInitialSPARender && this._renderFallback());
  }

  /**
   * Cleanup after slot removal.
   */
  _removeSlot() {
    this._clearCachedHtml();
  }
}

export { MerkurSlot, MerkurWidget };
